import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useLang } from "@/lib/lang";
import { Mic, BarChart3, Camera, Trophy, ArrowRight } from "lucide-react";

const heroBg = "https://images.unsplash.com/photo-1627931539006-d5c4677e05ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxwdWJsaWMlMjBzcGVha2VyJTIwc3RhZ2UlMjBkYXJrfGVufDB8fHx8MTc4MjY4NTE5Mnww&ixlib=rb-4.1.0&q=85";

export default function Landing() {
    const { t } = useLang();
    return (
        <div className="min-h-screen bg-ink text-slate-900">
            <Navbar />
            {/* Hero */}
            <section className="relative overflow-hidden border-b border-slate-200" data-testid="hero-section">
                <div
                    className="absolute inset-0 opacity-25"
                    style={{
                        backgroundImage: `url(${heroBg})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-white/70 to-white" />
                <div className="grain absolute inset-0" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-32 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-8">
                        <div className="mono-label mb-8" data-testid="hero-badge">
                            <span className="inline-block w-8 h-px bg-brand mr-3 align-middle" />
                            {t("hero.badge")}
                        </div>
                        <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-medium leading-[0.95] tracking-tighter mb-8" data-testid="hero-title">
                            {t("hero.title_a")}
                            <br />
                            <span className="text-brand">{t("hero.title_b")}</span>
                        </h1>
                        <p className="text-lg md:text-xl text-slate-600 max-w-2xl mb-10 font-light leading-relaxed">
                            {t("hero.subtitle")}
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link to="/register" className="btn-primary" data-testid="hero-cta-primary">
                                {t("hero.cta_primary")} <ArrowRight size={16} />
                            </Link>
                            <Link to="/pricing" className="btn-ghost" data-testid="hero-cta-secondary">
                                {t("hero.cta_secondary")}
                            </Link>
                        </div>
                    </div>
                    <div className="lg:col-span-4 lg:pl-8 lg:border-l border-slate-200 flex flex-col justify-end gap-6">
                        <div>
                            <div className="font-display text-6xl text-brand">20</div>
                            <div className="mono-label mt-2">{t("hero.stat_minutes")}</div>
                        </div>
                        <div className="divider" />
                        <div>
                            <div className="font-display text-6xl">3</div>
                            <div className="mono-label mt-2">{t("hero.stat_languages")}</div>
                        </div>
                        <div className="divider" />
                        <div>
                            <div className="font-display text-6xl">0</div>
                            <div className="mono-label mt-2">{t("hero.stat_video")}</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features */}
            <section id="features" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32" data-testid="features-section">
                <div className="mono-label mb-6">/ FEATURES</div>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tighter mb-16 max-w-3xl">
                    {t("features.title")}
                </h2>
                <div className="grid md:grid-cols-2 gap-px bg-slate-100 border border-slate-200">
                    <Feature icon={<Mic />} title={t("features.f1_title")} desc={t("features.f1_desc")} num="01" />
                    <Feature icon={<BarChart3 />} title={t("features.f2_title")} desc={t("features.f2_desc")} num="02" />
                    <Feature icon={<Camera />} title={t("features.f3_title")} desc={t("features.f3_desc")} num="03" />
                    <Feature icon={<Trophy />} title={t("features.f4_title")} desc={t("features.f4_desc")} num="04" />
                </div>
            </section>

            {/* CTA */}
            <section className="border-t border-slate-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 text-center">
                    <h3 className="font-display text-4xl md:text-5xl mb-6">
                        {t("hero.title_a")} <span className="text-brand">{t("hero.title_b")}</span>
                    </h3>
                    <Link to="/register" className="btn-primary" data-testid="cta-bottom">
                        {t("hero.cta_primary")} <ArrowRight size={16} />
                    </Link>
                </div>
            </section>

            <Footer />
        </div>
    );
}

function Feature({ icon, title, desc, num }) {
    return (
        <div className="bg-ink p-10 lg:p-14 group hover:bg-slate-50 transition-colors">
            <div className="flex items-start gap-6">
                <div className="mono-label text-brand">{num}</div>
                <div className="flex-1">
                    <div className="w-12 h-12 border border-slate-300 flex items-center justify-center mb-6 group-hover:border-brand transition-colors">
                        {React.cloneElement(icon, { size: 20, className: "text-slate-900" })}
                    </div>
                    <h3 className="font-display text-2xl mb-3 tracking-tight">{title}</h3>
                    <p className="text-slate-600 text-base leading-relaxed font-light">{desc}</p>
                </div>
            </div>
        </div>
    );
}
